/**
 * Pantry React Query Hooks
 *
 * With Better Auth, session cookies are sent automatically.
 * No token passing needed - just call the API functions directly.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { MatchProvenance } from "@batch-and-bowl/shared";
import * as pantryApi from "../api/pantry";
import type {
  PantryListItem,
  AddPantryItemRequest,
  UpdatePantryItemRequest,
  BulkAddPantryRequest,
  PantryEventOutcome,
} from "../api/types";
import { track } from "../analytics";
import { GROCERY_LISTS_QUERY_KEY } from "./query-keys";

export const PANTRY_QUERY_KEY = "pantry";
export const PANTRY_EXPIRING_QUERY_KEY = "pantry-expiring";
export const PANTRY_LOW_STOCK_QUERY_KEY = "pantry-low-stock";
export const PANTRY_STATS_QUERY_KEY = "pantry-stats";
export const INGREDIENTS_BY_CATEGORY_KEY = "ingredients-by-category";

/**
 * Fetch all pantry items for the current user
 */
export function usePantryItems() {
  return useQuery({
    queryKey: [PANTRY_QUERY_KEY],
    queryFn: () => pantryApi.getPantryItems(),
  });
}

/**
 * Fetch household pantry items expiring soon (the terracotta "Cook these
 * first" card's data source).
 */
export function usePantryExpiring() {
  return useQuery({
    queryKey: [PANTRY_EXPIRING_QUERY_KEY],
    queryFn: () => pantryApi.getExpiringPantryItems(),
  });
}

/**
 * Fetch household pantry items running low (the sage "Running low" card's
 * data source).
 */
export function usePantryLowStock() {
  return useQuery({
    queryKey: [PANTRY_LOW_STOCK_QUERY_KEY],
    queryFn: () => pantryApi.getLowStockPantryItems(),
  });
}

/**
 * Fetch the "This month" waste/reuse stats card's data (issue #377, B5).
 */
export function usePantryStats() {
  return useQuery({
    queryKey: [PANTRY_STATS_QUERY_KEY],
    queryFn: () => pantryApi.getPantryStats(),
  });
}

/**
 * Every pantry-mutating hook below invalidates all pantry query keys (not
 * just PANTRY_QUERY_KEY) -- a quantity edit or delete can change whether an
 * item still qualifies as expiring-soon/low-stock, and a delete now also
 * writes a PantryItemEvent (issue #377, B5), so the "This month" stats card
 * must not go stale after a pantry write either.
 */
function invalidatePantryQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  // Pantry quantities feed the read-time grocery purchase projection. The
  // broad key reaches every selected meal-plan/list scope without guessing a
  // plan id that this hook does not own.
  queryClient.invalidateQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [PANTRY_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [PANTRY_EXPIRING_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [PANTRY_LOW_STOCK_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [PANTRY_STATS_QUERY_KEY] });
}

/**
 * Fetch ingredients grouped by category (for quick-select UI)
 */
export function useIngredientsByCategory() {
  return useQuery({
    queryKey: [INGREDIENTS_BY_CATEGORY_KEY],
    queryFn: () => pantryApi.getIngredientsByCategory(),
    staleTime: 10 * 60 * 1000, // 10 minutes — ingredient catalog rarely changes
  });
}

/**
 * Add a single item to the pantry
 */
export function useAddPantryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddPantryItemRequest) => pantryApi.addPantryItem(data),
    onSuccess: () => {
      invalidatePantryQueries(queryClient);
    },
  });
}

// Epic #738 slice S2 (issue #742): the review batch's post-scan summary.
// `analytics` rides on the mutation VARIABLES (never the request body --
// mutationFn below destructures only `items` for POST /pantry/bulk) so
// PantryIntake.tsx's handleAddToPantry can hand this hook everything it
// computed from the full review-row state without that state ever crossing
// the network. Mirrors use-recipe-import.ts's track()-in-onSuccess pattern.
export interface BulkAddPantryReviewAnalytics {
  mode: "shelf" | "receipt" | "barcode";
  editedCount: number;
  /** Rows whose matched ingredient identity changed during review --
   * "relabeledCount", not the issue's proposed "renamedCount" (see
   * analytics.ts's PRIVATE_PROPERTY_KEY guard test for why). */
  relabeledCount: number;
  rejectedCount: number;
  provenanceCounts: Record<MatchProvenance, number>;
}

/**
 * Bulk add items to the pantry
 */
export function useBulkAddPantryItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      data: BulkAddPantryRequest & { analytics?: BulkAddPantryReviewAnalytics },
    ) => pantryApi.bulkAddPantryItems({ items: data.items }),
    onSuccess: (savedItems, variables) => {
      invalidatePantryQueries(queryClient);
      if (variables.analytics) {
        const acceptedCount = Math.max(
          0,
          savedItems.length -
            variables.analytics.editedCount -
            variables.analytics.relabeledCount,
        );
        track("pantry_intake_reviewed", {
          mode: variables.analytics.mode,
          acceptedCount,
          editedCount: variables.analytics.editedCount,
          relabeledCount: variables.analytics.relabeledCount,
          rejectedCount: variables.analytics.rejectedCount,
          provenanceCounts: variables.analytics.provenanceCounts,
        });
      }
    },
  });
}

/**
 * #388. Invalidates the grocery lists as well as the pantry: accepting a
 * guess changes which bucket the line lands in on the next read, so a
 * pantry-only invalidation would leave the list showing the guess it just
 * accepted.
 */
export function useAcceptStapleGuesses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ingredientIds: string[]) =>
      pantryApi.acceptStapleGuesses(ingredientIds),
    onSuccess: () => {
      invalidatePantryQueries(queryClient);
    },
  });
}

/**
 * Update a pantry item
 */
export function useUpdatePantryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePantryItemRequest }) =>
      pantryApi.updatePantryItem(id, data),
    onSuccess: () => {
      invalidatePantryQueries(queryClient);
    },
  });
}

/**
 * Remove a single item from the pantry. `outcome` is optional (issue #377,
 * B5) -- see removePantryItem in lib/api/pantry.ts for what omitting it
 * does server-side. Variables are always `{ id, outcome? }` (not a bare
 * string) so the outcome can travel alongside the id through the mutation.
 */
export function useRemovePantryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome?: PantryEventOutcome }) =>
      pantryApi.removePantryItem(id, outcome),
    onMutate: async ({ id }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: [PANTRY_QUERY_KEY] });

      // Snapshot previous value
      const previous = queryClient.getQueryData<PantryListItem[]>([
        PANTRY_QUERY_KEY,
      ]);

      // Optimistically remove the item
      queryClient.setQueryData<PantryListItem[]>([PANTRY_QUERY_KEY], (old) => {
        if (!old) return old;
        return old.filter((item) => item.id !== id);
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData([PANTRY_QUERY_KEY], context.previous);
      }
    },
    onSettled: () => {
      invalidatePantryQueries(queryClient);
    },
  });
}

/**
 * Clear all pantry items
 */
export function useClearPantry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => pantryApi.clearPantry(),
    onSuccess: () => {
      queryClient.setQueryData([PANTRY_QUERY_KEY], []);
      invalidatePantryQueries(queryClient);
    },
  });
}
